import { Injectable } from '@angular/core';
import { MeasuresLoaderService } from '@library/store/measures/measures-loader.service';
import { EnumMeasureTypes, TimeRange } from '@library/store/measures/measures.interface';
import { ModulesFacade } from '@library/store/modules/modules.facade';
import { CpCompareScaleService } from '@pages/control-page/control-page-main/control-page-compare/cp-compare-header/cp-compare-scale/cp-compare-scale.service';
import { CpCompareTimeRangeService } from '@pages/control-page/control-page-main/control-page-compare/cp-compare-header/cp-compare-timerange/cp-compare-timerange.service';
import { MeasureCard, YScaleType } from '@pages/control-page/control-page-main/control-page-compare/cp-compare-sidebar/cp-compare-sidebar.service';
import { firstValueFrom } from 'rxjs';

@Injectable()
export class ControlPageCompareService {

  constructor(
    private measuresLoaderService: MeasuresLoaderService,
    private compareTimeRangeService: CpCompareTimeRangeService,
    private compareScaleService: CpCompareScaleService,
    private modulesFacade: ModulesFacade,
  ) {}

  async loadMeasures(measureCards: MeasureCard[]) {
    const firstTimeRange = await firstValueFrom(this.compareTimeRangeService.firstTimeRange$);
    this.loadMeasuresForTimeRange(measureCards, firstTimeRange);

    const secondTimeRange = await firstValueFrom(this.compareTimeRangeService.secondTimeRange$);
    this.loadMeasuresForTimeRange(measureCards, secondTimeRange);
  }

  async loadMeasuresForTimeRange(measureCards: MeasureCard[], timeRange: TimeRange) {
    if (!timeRange) {
      return;
    }

    const apiScale = await firstValueFrom(this.compareScaleService.apiScale$);

    const moduleCards = measureCards.filter(m => m.module);
    const homeCards = measureCards.filter(m => m.home);
    const roomCards = measureCards.filter(m => m.room);

    this.measuresLoaderService.loadModulesMeasures(
      moduleCards.map(m => m.module),
      moduleCards.reduce((acc, curr) => {
        if (acc[curr.module.id]) {
          acc[curr.module.id].push(curr.measureType);
        }
        else {
          acc[curr.module.id] = [curr.measureType];
        }

        if (curr.measureType === EnumMeasureTypes.SUM_BOILER_ON) {
          acc[curr.module.id].push(EnumMeasureTypes.SUM_BOILER_OFF);
        }

        return acc;
      }, {} as Record<string, EnumMeasureTypes[]>),
      apiScale,
      timeRange,
    );

    if (roomCards.length > 0) {
      this.measuresLoaderService.loadRoomsMeasures(
        roomCards.map(card => card.room),
        roomCards.reduce((acc, curr) => {
          if (acc[curr.room.id]) {
            acc[curr.room.id].push(curr.measureType);
          }
          else {
            acc[curr.room.id] = [curr.measureType];
          }

          return acc;
        }, {} as Record<string, EnumMeasureTypes[]>),
        apiScale,
        timeRange,
      );
    }

    if (homeCards.some(c => [YScaleType.ELECTRICITY, YScaleType.PRICE].includes(c.yScaleType))) {
      this.measuresLoaderService.loadHomeElecMeasures([
        EnumMeasureTypes.SUM_ENERGY_ELEC_NO_CONTRACT,
        EnumMeasureTypes.SUM_ENERGY_ELEC_BASE,
        EnumMeasureTypes.SUM_ENERGY_ELEC_PEAK,
        EnumMeasureTypes.SUM_ENERGY_ELEC_OFFPEAK,
        EnumMeasureTypes.SUM_ENERGY_PRICE_NO_CONTRACT,
        EnumMeasureTypes.SUM_ENERGY_PRICE_BASE,
        EnumMeasureTypes.SUM_ENERGY_PRICE_PEAK,
        EnumMeasureTypes.SUM_ENERGY_PRICE_OFFPEAK,
        EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_NO_CONTRACT,
        EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_BASE,
        EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PEAK,
        EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_OFFPEAK,
        EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_NO_CONTRACT,
        EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_BASE,
        EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_PEAK,
        EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_OFFPEAK,
      ], apiScale, timeRange);

      const appliances = await firstValueFrom(this.modulesFacade.appliances$);
      this.measuresLoaderService.loadModulesMeasures(
        appliances,
        appliances.reduce((acc, curr) => {
          acc = {
            ...acc,
            [curr.id]: [
              EnumMeasureTypes.SUM_ENERGY_ELEC_NO_CONTRACT,
              EnumMeasureTypes.SUM_ENERGY_ELEC_BASE,
              EnumMeasureTypes.SUM_ENERGY_ELEC_PEAK,
              EnumMeasureTypes.SUM_ENERGY_ELEC_OFFPEAK,
              EnumMeasureTypes.SUM_ENERGY_PRICE_NO_CONTRACT,
              EnumMeasureTypes.SUM_ENERGY_PRICE_BASE,
              EnumMeasureTypes.SUM_ENERGY_PRICE_PEAK,
              EnumMeasureTypes.SUM_ENERGY_PRICE_OFFPEAK,
              EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_NO_CONTRACT,
              EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_BASE,
              EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PEAK,
              EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_OFFPEAK,
              EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_NO_CONTRACT,
              EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_BASE,
              EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_PEAK,
              EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_OFFPEAK,
            ],
          };
          return acc;
        }, {}),apiScale, timeRange);
    }
  }
}
